import "./globals.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { createRoot } from "react-dom/client";
import { StrictMode } from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/initializers/query-client";
import { authenticatedRoutes, unauthenticatedRoutes } from "./routes";
import { useAuth } from "./hooks/use-auth";
import { LoadingSpinner } from "./components/ui/spinner";
import { SidebarProvider } from "./components/ui/sidebar";
import { AppSidebar } from "./components/layout/app-sidebar";
import { Toaster } from "./components/ui/toaster";
import { ErrorBoundary } from "./components/error-boundary";
import { ImportProgressProvider } from "@/api/imports";

const AuthenticatedApp = () => {
  const defaultOpen = localStorage.getItem("sidebarCollapsed") !== "true";

  return (
    <ImportProgressProvider>
      <SidebarProvider defaultOpen={defaultOpen}>
        <AppSidebar />
        <main className="h-full w-full">
          <Routes>
            {authenticatedRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
          </Routes>
        </main>
      </SidebarProvider>
    </ImportProgressProvider>
  );
};

const UnauthenticatedApp = () => {
  return (
    <Routes>
      {unauthenticatedRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={route.element} />
      ))}
    </Routes>
  );
};

export const App = () => {
  const { currentUser, isCurrentUserLoading } = useAuth();
  const location = useLocation();

  if (isCurrentUserLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="w-[200px]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary resetKey={location.pathname}>
      {currentUser ? <AuthenticatedApp /> : <UnauthenticatedApp />}
      <Toaster />
    </ErrorBoundary>
  );
};

const root = document.getElementById("root");
if (!root) throw new Error("Root element not found");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </StrictMode>,
);
